import { useCallback, useEffect, useRef } from "react";
import { useTranslations } from "@zapier/i18n";
import { Text } from "@zapier/design-system";
import {
  Connection,
  getMessageFromNetworkError,
  useConvertToManaged,
  useRenameAuthentication,
} from "@zapier/client";
import { useNotification } from "@zapier/utils";

type NetworkError = Parameters<typeof getMessageFromNetworkError>[0];

interface Params {
  id?: number;
  connection?: Connection;
  isOpen: boolean;
  onConverted: () => void;
}

export interface ConvertToManagedValues {
  name: string;
  shareBackToPreviousOwner: boolean;
}

/** Converts the connection, then renames it if the name changed, reporting each outcome via notifications. */
export function useConvertToManagedSubmit({
  id,
  connection,
  isOpen,
  onConverted,
}: Params) {
  const t = useTranslations("connections-convert-to-managed.modal");
  const { createNotification } = useNotification();
  const { convertToManagedAsync, isLoading } = useConvertToManaged();
  const { rename } = useRenameAuthentication();
  const openConnectionId = useRef(isOpen ? id : undefined);

  useEffect(() => {
    openConnectionId.current = isOpen ? id : undefined;
  }, [id, isOpen]);

  const getErrorMessage = useCallback(
    (error: NetworkError) =>
      getMessageFromNetworkError(error) || t("notifications.error-generic"),
    [t],
  );

  const notifySuccess = useCallback(
    (connectionTitle: string) =>
      createNotification({
        message: <Text>{t("notifications.success", { connectionTitle })}</Text>,
        type: "success",
      }),
    [createNotification, t],
  );

  const submit = useCallback(
    async ({ name, shareBackToPreviousOwner }: ConvertToManagedValues) => {
      if (!id || !connection) {
        return;
      }
      const connectionTitle = connection.title ?? "";

      try {
        await convertToManagedAsync({ id, shareBackToPreviousOwner });
      } catch (error) {
        createNotification({
          title: t("notifications.error-title", { connectionTitle }),
          message: getErrorMessage(error as NetworkError),
          type: "error",
        });
        return;
      }

      // The dialog may have been closed, or swapped for another, while the request ran.
      if (openConnectionId.current === id) {
        onConverted();
      }

      if (name === connectionTitle.trim()) {
        notifySuccess(connectionTitle);
        return;
      }

      rename(
        { id, name },
        {
          onSuccess: () => notifySuccess(name),
          onError: (error) =>
            createNotification({
              title: t("notifications.rename-failed"),
              message: getErrorMessage(error),
              type: "error",
            }),
        },
      );
    },
    [
      connection,
      convertToManagedAsync,
      createNotification,
      getErrorMessage,
      id,
      notifySuccess,
      onConverted,
      rename,
      t,
    ],
  );

  return { submit, isLoading };
}
